/**
 * useNotifications.js - Hook para Web Push Notifications
 * CG Business ERP - P4
 * 
 * Features:
 * - Gestão de permissões do browser
 * - Polling de notificações pendentes
 * - Exibição de notificações nativas
 * - Integração com API de notificações
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationsAPI } from '@/lib/api';
import { useAuth } from './useAuth';

// Polling interval for pending notifications (15 seconds)
const POLL_INTERVAL = 15000;

// Notification icons by type
const NOTIFICATION_ICONS = {
  missed_checkpoint: '⏰',
  no_opening: '🚫',
  no_closing: '⚠️',
  cash_variance: '💰'
};

export function useNotifications() {
  const { user } = useAuth();
  const [permission, setPermission] = useState('default');
  const [isEligible, setIsEligible] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const pollIntervalRef = useRef(null);
  const startPollingRef = useRef(null);
  const stopPollingRef = useRef(null);
  const showBrowserNotificationRef = useRef(null);
  
  // Check if Web Notifications are supported
  const isSupported = 'Notification' in window;
  
  // Check eligibility and subscription status
  const checkStatus = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await notificationsAPI.getStatus();
      setIsEligible(response.data.is_eligible);
      setIsSubscribed(response.data.is_subscribed);
      
      // Update browser permission state
      if (isSupported) {
        setPermission(Notification.permission);
      }
    } catch (error) {
      console.error('[useNotifications] Failed to check status:', error);
    } finally {
      setLoading(false);
    }
  }, [user, isSupported]);
  
  // Request browser notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      return { success: false, error: 'Browser não suporta notificações' };
    }
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        return { success: true };
      } else if (result === 'denied') {
        return { success: false, error: 'Notificações bloqueadas. Verifique as definições do browser.' };
      } else {
        return { success: false, error: 'Permissão não concedida' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [isSupported]);
  
  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !isEligible) {
      return { success: false, error: 'Não elegível para notificações' };
    }
    
    // First request permission
    if (permission !== 'granted') {
      const permResult = await requestPermission();
      if (!permResult.success) {
        return permResult;
      }
    }
    
    try {
      // Get browser info
      const browserInfo = {
        userAgent: navigator.userAgent,
        browser: getBrowserName()
      };
      
      // Subscribe via API
      const response = await notificationsAPI.subscribe({
        endpoint: 'browser-notification',
        browser: browserInfo.browser
      });
      
      setIsSubscribed(true);
      
      // Start polling for notifications
      startPollingRef.current?.();
      
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('[useNotifications] Subscribe error:', error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }, [isSupported, isEligible, permission, requestPermission]);
  
  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    try {
      await notificationsAPI.unsubscribe();
      setIsSubscribed(false);
      stopPollingRef.current?.();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);
  
  // Poll for pending notifications
  const pollNotifications = useCallback(async () => {
    if (!isSubscribed || !isEligible) return;
    
    try {
      const response = await notificationsAPI.getPending();
      const notifications = response.data || [];
      
      // Show new notifications
      notifications.forEach(async (notif) => {
        // Check if we've already shown this notification
        const shownKey = `notif_shown_${notif.id}`;
        if (sessionStorage.getItem(shownKey)) return;
        
        // Show browser notification
        if (permission === 'granted') {
          showBrowserNotificationRef.current?.(notif);
          sessionStorage.setItem(shownKey, 'true');
          
          // Mark as sent
          try {
            await notificationsAPI.markSent(notif.id);
          } catch (e) {
            console.warn('Failed to mark notification as sent');
          }
        }
      });
      
      setPendingNotifications(notifications);
    } catch (error) {
      console.error('[useNotifications] Poll error:', error);
    }
  }, [isSubscribed, isEligible, permission]);
  
  // Start polling interval
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    
    // Poll immediately
    pollNotifications();
    
    // Then poll at interval
    pollIntervalRef.current = setInterval(pollNotifications, POLL_INTERVAL);
  }, [pollNotifications]);
  
  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);
  
  // Show browser notification
  const showBrowserNotification = useCallback((notif) => {
    if (permission !== 'granted') return;
    
    const icon = NOTIFICATION_ICONS[notif.alert_type] || '🔔';
    
    const notification = new Notification(notif.title, {
      body: notif.body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: notif.id,
      requireInteraction: true,
      data: {
        alert_id: notif.alert_id,
        url: '/cash' // Navigate to cash management
      }
    });
    
    // Handle click - navigate to alerts
    notification.onclick = () => {
      window.focus();
      window.location.href = '/cash?tab=alerts';
      notification.close();
    };
  }, [permission]);

  startPollingRef.current = startPolling;
  stopPollingRef.current = stopPolling;
  showBrowserNotificationRef.current = showBrowserNotification;
  
  // Dismiss a notification
  const dismissNotification = useCallback(async (notificationId) => {
    try {
      await notificationsAPI.dismiss(notificationId);
      setPendingNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  }, []);
  
  // Initialize on mount
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);
  
  // Start/stop polling based on subscription
  useEffect(() => {
    if (isSubscribed && isEligible && permission === 'granted') {
      startPolling();
    } else {
      stopPolling();
    }
    
    return () => stopPolling();
  }, [isSubscribed, isEligible, permission, startPolling, stopPolling]);
  
  return {
    // State
    isSupported,
    permission,
    isEligible,
    isSubscribed,
    loading,
    pendingNotifications,
    
    // Actions
    requestPermission,
    subscribe,
    unsubscribe,
    dismissNotification,
    checkStatus
  };
}

// Helper: Get browser name
function getBrowserName() {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  return 'Unknown';
}

export default useNotifications;
